import { memo } from "react";

import {
  type Maybe,
  type Image as ShopifyImage,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Heading } from "@react/components/Heading";
import { Image } from "@react/components/Image";
import { Text } from "@react/components/Text";
import { CollectionBreadcrumbs } from "@sections/Collection/CollectionBreadcrumbs";
import { type TTailwindString, tw } from "classnames";

type Props = {
  breadcrumbsLabel: string;
  description: Maybe<string>;
  heroImage: Maybe<ShopifyImage>;
  homeLabel: string;
  title: Maybe<string>;
};

const styles = {
  base: tw(
    "mg:flex",
    "max-w-screen-xl",
    "sm:pt-4",
    "mg:pt-12",
    "mg:px-12",
    "mx-auto"
  ),
  content: (hasHeroImage: boolean) =>
    tw(
      "flex",
      "flex-col",
      "gap-y-6",
      "mg:w-full",
      "pt-4",
      "sm:pt-6",
      "p-6",
      "mg:p-0",
      "mg:pr-10",
      {
        "pb-4": !hasHeroImage,
        "pb-6": hasHeroImage,
      }
    ),
  textContainer: tw("flex", "flex-col", "justify-center", "h-full"),
  title: tw("text-primary", "mb-1", "md:mb-2"),
  description: tw("text-secondary"),
  heroImageContainer: tw(
    "w-full",
    "mg:w-3/5",
    "shrink-0",
    "aspect-[1024/320]" as TTailwindString,
    "mg:aspect-[812/300]" as TTailwindString
  ),
  heroImage: tw("object-cover", "w-full", "h-full", "animate-fade-in"),
};

const CollectionHeroViewBase = ({
  breadcrumbsLabel,
  description,
  heroImage,
  homeLabel,
  title,
}: Props) => {
  return (
    <div className={styles.base}>
      <div className={styles.content(!!heroImage)}>
        <CollectionBreadcrumbs
          breadcrumbsLabel={breadcrumbsLabel}
          homeLabel={homeLabel}
        />
        <div className={styles.textContainer}>
          <Heading as="h3" className={styles.title}>
            {title}
          </Heading>
          <Text as="body1" className={styles.description}>
            {description}
          </Text>
        </div>
      </div>
      {heroImage && (
        <div className={styles.heroImageContainer}>
          <Image
            className={styles.heroImage}
            data={heroImage}
            loading="eager"
            width={1024}
          />
        </div>
      )}
    </div>
  );
};

export const CollectionHeroView = memo(CollectionHeroViewBase);
